/**
 * IONLOOP Assistant — lightweight tokenizer.
 *
 * English product/support question tokenizer with n-gram phrase support.
 * Small, deterministic, no external NLP dependency.
 */

const APOSTROPHE_LIKE = /[\u2018\u2019\u02BC\u2032\u201A]/g;
const QUOTE_LIKE = /[\u201C\u201D\u201E]/g;
const DASH_LIKE = /[\u2013\u2014\u2212]/g;

/**
 * Split text into lowercase word tokens. Apostrophes are normalized away so
 * "it's" and "its" match the same token ("its").
 */
function tokenize(text) {
    if (!text) {
        return [];
    }
    return String(text)
        .toLowerCase()
        .replace(APOSTROPHE_LIKE, '')
        .replace(QUOTE_LIKE, ' ')
        .replace(DASH_LIKE, ' ')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
        .split(/\s+/)
        .filter(function (tok) {
            return tok.length > 0;
        });
}

/**
 * Return n-grams (joined phrases) from a token array.
 */
function phraseNGrams(tokens, minLen, maxLen) {
    const out = [];
    if (!tokens || tokens.length === 0) {
        return out;
    }
    const min = minLen || 2;
    const max = maxLen || 3;
    for (let n = min; n <= Math.min(max, tokens.length); n += 1) {
        for (let i = 0; i <= tokens.length - n; i += 1) {
            out.push(tokens.slice(i, i + n).join(' '));
        }
    }
    return out;
}

/**
 * Light English stemming for matching purposes (keeps plural recall without a
 * heavy NLP dependency). Conservative and safe:
 *   - ies -> y            (policies -> policy)
 *   - sses -> ss          (classes -> class)
 *   - xes/ches/shes/zes -> x/ch/sh/z  (boxes, churches, wishes, quizzes)
 *   - trailing s -> ''    (trades -> trade, prices -> price, fees -> fee)
 * Words that are genuinely short or irregular are left alone.
 */
function stem(token) {
    if (!token || token.length <= 3) {
        return token;
    }
    if (/ies$/.test(token) && token.length > 4) {
        return token.slice(0, -3) + 'y';
    }
    if (/sses$/.test(token)) {
        return token.slice(0, -2);
    }
    if (/(x|ch|sh|z)es$/.test(token)) {
        return token.slice(0, -2);
    }
    if (/s$/.test(token) && !/ss$/.test(token) && !/us$/.test(token)) {
        return token.slice(0, -1);
    }
    return token;
}

/**
 * Normalize a raw phrase for dictionary matching (single spaces, lowercase).
 */
function normalizePhrase(phrase) {
    if (!phrase) {
        return '';
    }
    return String(phrase)
        .toLowerCase()
        .replace(APOSTROPHE_LIKE, '')
        .replace(QUOTE_LIKE, ' ')
        .replace(DASH_LIKE, ' ')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
}

module.exports = {
    tokenize: tokenize,
    phraseNGrams: phraseNGrams,
    normalizePhrase: normalizePhrase,
    stem: stem
};
